import {
  Button,
  Group,
  ScrollArea,
  Stack,
  Text,
  Title,
  Badge,
  Modal,
  TextInput,
  Loader,
  Paper,
  Table,
  Center,
  Tooltip
} from '@mantine/core';
import DataTables, { DataTableColumn } from 'components/DataTables';
import Card from 'components/Card';
import React, { useEffect, useMemo, useState } from 'react';
import useDebounce from 'hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { trainingCatalogueService } from 'services/lcms/trainingCatalogue.service';
import { trainingCatalogueParticipantsService } from 'services/lcms/trainingCatalogue.service';
import { pathnames } from 'routes/lcms';
import { useCourseStore } from 'store/createCourseStore';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import {
  trainingCatalogueDetailService,
  trainingCatalogueCompetencyDetailService
} from 'services/lcms/trainingCatalogue.service';
import { HiOutlineEye } from 'react-icons/hi';

const LOCAL_KEY = 'courseCombineCompareIds';

// Helper function to safely decode HTML entities and strip tags
const stripHtmlAndDecode = (html: string): string => {
  if (!html) return '';

  // Create a temporary DOM element to safely decode HTML entities
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;

  // Get text content (this automatically decodes entities and strips tags)
  return (tempElement.textContent || tempElement.innerText || '').replace(/\s+/g, ' ').trim();
};

// Helper function to check if a value appears in multiple items (should be highlighted)
const shouldHighlight = (
  fieldKey: string,
  currentValue: any,
  allData: any[],
  competencyData?: any[]
) => {
  if (!currentValue || currentValue === '-' || currentValue === '') return false;

  let matchCount = 0;

  // Check main data
  if ((fieldKey.includes('competency') || fieldKey.includes('urgency')) && competencyData) {
    // For competency fields, check competencyData
    competencyData.forEach((item) => {
      if (item && item[fieldKey] === currentValue) {
        matchCount++;
      }
    });
  } else {
    // For regular fields, check main data
    allData.forEach((item) => {
      if (item && item[fieldKey] === currentValue) {
        matchCount++;
      }
    });
  }

  // Highlight if the value appears in 2 or more items
  return matchCount >= 2;
};

// Helper function to get highlight background color
const getHighlightStyle = (shouldHighlight: boolean) => {
  if (!shouldHighlight) return {};

  return {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '2px 6px',
    fontWeight: 600
  };
};

const fieldGroups = [
  {
    title: 'Basic Information',
    fields: [
      { key: 'programName', label: 'Program' },
      { key: 'trainingCategoryDesc', label: 'Category' },
      { key: 'trainingMethodName', label: 'Method' },
      {
        key: 'learningHours',
        label: 'Hours',
        render: (v: number) => (v ? `${v} hours` : '-')
      }
    ]
  },
  {
    title: 'Financial Information',
    fields: [
      {
        key: 'budget',
        label: 'Budget per Person',
        render: (v: number) => (v ? `Rp ${v.toLocaleString()}` : '-')
      },
      {
        key: 'expectedParticipants',
        label: 'Expected Participants',
        render: (v: number, item: any) => {
          const totalCost = item?.totalCost || 0;
          const budget = item?.budget || 0;
          if (totalCost && budget) {
            const calculated = Math.round(totalCost / budget);
            return `${calculated} people`;
          }
          return v ? `${v} people` : '-';
        }
      },
      {
        key: 'totalBudget',
        label: 'TOTAL COST (BUDGET * Count Participant)',
        render: (v: number, item: any) => {
          const totalCost = item?.totalCost || 0;
          if (totalCost) {
            return `Rp ${totalCost.toLocaleString()}`;
          }
          const budget = item?.budget || 0;
          const participants = item?.expectedParticipants || 0;
          if (budget && participants) {
            return `Rp ${(budget * participants).toLocaleString()}`;
          }
          return v ? `Rp ${v.toLocaleString()}` : '-';
        }
      }
    ]
  },
  {
    title: 'Competency & Strategy',
    fields: [
      { key: 'competencyName', label: 'Competency' },
      { key: 'strategicObjectives', label: 'Strategic Objectives' },
      { key: 'performanceMeasures', label: 'Performance Measures' },
      { key: 'levelImpactDesc', label: 'Impact Level' }
    ]
  },
  {
    title: 'Schedule & Provider',
    fields: [
      { key: 'scheduleDesc', label: 'Schedule' },
      { key: 'trainingProvider', label: 'Provider' },
      { key: 'trainingModelDesc', label: 'Training Model' }
    ]
  }
];

const CourseCombineComparePage: React.FC = () => {
  const navigate = useNavigate();
  const { setFormMode, reset } = useCourseStore();
  const [ids, setIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [participantModalOpen, setParticipantModalOpen] = useState(false);
  const [participantTrainingId, setParticipantTrainingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const [modalSize] = useState(6);
  const debouncedSearch = useDebounce(search, 400);

  const { data: modalData, isFetching: modalLoading } = useQuery({
    queryKey: [
      'trainingCatalogue-modal',
      { search: debouncedSearch, page: modalPage, size: modalSize }
    ],
    queryFn: () =>
      trainingCatalogueService.list({
        search: debouncedSearch,
        pageNumber: modalPage,
        pageSize: modalSize
      }),
    enabled: modalOpen
  });

  // Participants query for participant modal
  const { data: participantsRes, isPending: isParticipantsPending } = useQuery({
    queryKey: ['trainingCatalogueParticipants', participantTrainingId],
    queryFn: () => trainingCatalogueParticipantsService.list(participantTrainingId || ''),
    enabled: !!participantTrainingId && participantModalOpen
  });

  const participants = participantsRes?.data || [];

  const participantColumns: DataTableColumn[] = [
    { heading: 'Name', props: 'participantName', id: 'participantName', active: true },
    { heading: 'Position', props: 'participantPosition', id: 'participantPosition', active: true },
    { heading: 'Binusian ID', props: 'binusianId', id: 'binusianId', active: true },
    { heading: 'Type', props: 'participantType', id: 'participantType', active: true },
    { heading: 'Level', props: 'participantLevel', id: 'participantLevel', active: true }
  ];

  const openParticipantModal = (trainingId: string) => {
    setParticipantTrainingId(trainingId);
    setParticipantModalOpen(true);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      const parsed: string[] = raw ? JSON.parse(raw) : [];
      if (!parsed || parsed.length < 2) {
        navigate(pathnames.courseCombineTNA);
      } else {
        setIds(parsed.slice(0, 12));
      }
    } catch {
      navigate(pathnames.courseCombineTNA);
    }
  }, [navigate]);

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['trainingCatalogueDetail', id],
      queryFn: () => trainingCatalogueDetailService.detail(id),
      enabled: !!id
    }))
  });

  const competencyQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['trainingCatalogueCompetencyDetail', id],
      queryFn: () => trainingCatalogueCompetencyDetailService.detail(id),
      enabled: !!id
    }))
  });

  const loading = queries.some((q) => q.isPending) || competencyQueries.some((q) => q.isPending);
  const data = queries.map((q) => q.data?.data);
  const competencyData = competencyQueries.map((q) => q.data?.data);

  const clearAll = () => {
    localStorage.removeItem(LOCAL_KEY);
    navigate(pathnames.courseCombineTNA);
  };

  const removeId = (id: string) => {
    const next = ids.filter((x) => x !== id);
    if (next.length < 2) {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      navigate(pathnames.courseCombineTNA);
      return;
    }
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
    setIds(next);
  };

  const header = useMemo(
    () => (
      <Stack gap={8} mb={8}>
        <Group justify='space-between' align='flex-start'>
          <div>
            <Title order={2} mb={4}>
              Compare Training Catalogue
            </Title>
            <Text fz={13} c='secondary'>
              Comparing {ids.length} item{ids.length !== 1 && 's'} (max 12). Remove or add more as
              needed.
            </Text>
          </div>
          <Group gap='xs' wrap='wrap'>
            <Button variant='default' onClick={() => navigate(pathnames.courseCombineTNA)}>
              Back to List
            </Button>
            <Button variant='default' onClick={clearAll}>
              Reset
            </Button>
            <Button
              variant='default'
              onClick={() => {
                // IMPORTANT ORDER:
                // We must persist the selected IDs BEFORE calling reset(), because reset() reads
                // and consumes (removes) 'tnaCoursesBuffer' from localStorage to hydrate store.
                localStorage.setItem('tnaCoursesBuffer', JSON.stringify(ids));

                // Reset and prepare create course with tnaCourses references
                reset();
                setFormMode('add');

                // Navigate after state update to ensure tnaCourses is loaded
                setTimeout(() => {
                  navigate(pathnames.createCourses);
                }, 0);
              }}
            >
              Create Course
            </Button>
            {ids.length < 12 && (
              <Button variant='filled' onClick={() => setModalOpen(true)}>
                Add More
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    ),
    [ids]
  );

  return (
    <Stack>
      {header}
      <Card>
        {loading && (
          <Group justify='center' py={60}>
            <Stack align='center' gap={16}>
              <Loader size='lg' variant='dots' />
              <Stack align='center' gap={4}>
                <Text fz={16} fw={600} c='dark.6'>
                  Loading comparison data...
                </Text>
                <Text fz={12} c='secondary.6'>
                  Fetching detailed training information and competency details
                </Text>
              </Stack>
            </Stack>
          </Group>
        )}
        {!loading && (
          <ScrollArea offsetScrollbars>
            <div style={{ minWidth: '100%', overflowX: 'auto' }}>
              {/* Basic Information Section */}
              <Paper
                p='sm'
                mb='xs'
                style={{ background: 'linear-gradient(90deg, #e3f2fd 0%, #f8f9fa 100%)' }}
              >
                <Group justify='space-between' align='center'>
                  <Text fz={14} fw={700} c='blue.7' tt='uppercase'>
                    Basic Information
                  </Text>
                  {data.length < 12 && (
                    <Button
                      variant='light'
                      color='blue'
                      size='xs'
                      onClick={() => setModalOpen(true)}
                      leftSection={<Text fz={12}>+</Text>}
                    >
                      Add Training
                    </Button>
                  )}
                </Group>
              </Paper>

              {/* Course Name Row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${data.length}, 1fr)`,
                  gap: '12px',
                  marginBottom: '8px',
                  minWidth: `${200 + data.length * 300}px`
                }}
              >
                <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                  <Text fz={12} fw={600} c='secondary.7'>
                    Course Name
                  </Text>
                </Paper>
                {data.map((col, idx) => (
                  <Paper
                    key={`course-name-${ids[idx]}`}
                    withBorder
                    p='sm'
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      position: 'relative'
                    }}
                  >
                    <Text fw={600} fz={12} lineClamp={2} c='dark.8' pr={20}>
                      {col?.courseName || '-'}
                    </Text>
                    <Tooltip
                      label='Remove this training from comparison'
                      position='top'
                      withArrow
                      color='red'
                    >
                      <Button
                        size='compact-xs'
                        variant='subtle'
                        color='red'
                        onClick={() => removeId(ids[idx])}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          opacity: 0.6,
                          transition: 'opacity 0.2s ease, background-color 0.2s ease',
                          minWidth: '18px',
                          minHeight: '18px',
                          padding: '0',
                          fontSize: '12px',
                          borderRadius: '50%'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.6';
                        }}
                      >
                        ×
                      </Button>
                    </Tooltip>
                  </Paper>
                ))}
              </div>

              {/* Module Code Row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${data.length}, 1fr)`,
                  gap: '12px',
                  marginBottom: '8px',
                  minWidth: `${200 + data.length * 300}px`
                }}
              >
                <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                  <Text fz={12} fw={600} c='secondary.7'>
                    Module Code
                  </Text>
                </Paper>
                {data.map((col, idx) => (
                  <Paper
                    key={`module-code-${ids[idx]}`}
                    withBorder
                    p='sm'
                    style={{
                      ...getHighlightStyle(shouldHighlight('moduleCode', col?.moduleCode, data))
                    }}
                  >
                    <Text fz={12} c='dark.7'>
                      {col?.moduleCode || '-'}
                    </Text>
                  </Paper>
                ))}
              </div>

              {/* PNA Year Row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${data.length}, 1fr)`,
                  gap: '12px',
                  marginBottom: '24px',
                  minWidth: `${200 + data.length * 300}px`
                }}
              >
                <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                  <Text fz={12} fw={600} c='secondary.7'>
                    PNA Year
                  </Text>
                </Paper>
                {data.map((col, idx) => (
                  <Paper
                    key={`pna-year-${ids[idx]}`}
                    withBorder
                    p='sm'
                    style={{
                      ...getHighlightStyle(shouldHighlight('pnaYear', col?.pnaYear, data))
                    }}
                  >
                    <Text fz={12} c='dark.7'>
                      {col?.pnaYear || '-'}
                    </Text>
                  </Paper>
                ))}
              </div>

              {/* Competency Section */}
              {competencyData.some((comp) => comp?.competencyCategoryDesc || comp?.urgencyDesc) && (
                <>
                  <Paper
                    p='sm'
                    mb='xs'
                    style={{ background: 'linear-gradient(90deg, #e3f2fd 0%, #f8f9fa 100%)' }}
                  >
                    <Text fz={14} fw={700} c='blue.7' tt='uppercase'>
                      Competency Information
                    </Text>
                  </Paper>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `200px repeat(${data.length}, 1fr) ${data.length < 12 ? '200px' : ''}`,
                      gap: '12px',
                      marginBottom: '16px',
                      minWidth: `${200 + data.length * 300 + (data.length < 12 ? 200 : 0)}px`
                    }}
                  >
                    <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                      <Text fz={12} fw={600} c='secondary.7'>
                        Competency Category
                      </Text>
                    </Paper>
                    {competencyData.map((competency, idx) => (
                      <Paper
                        key={`comp-cat-${ids[idx]}`}
                        withBorder
                        p='sm'
                        style={{
                          ...getHighlightStyle(
                            shouldHighlight(
                              'competencyCategoryDesc',
                              competency?.competencyCategoryDesc,
                              data,
                              competencyData
                            )
                          )
                        }}
                      >
                        <Text fz={12} c='dark.7'>
                          {competency?.competencyCategoryDesc || '-'}
                        </Text>
                      </Paper>
                    ))}
                    {data.length < 12 && <div></div>}
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `200px repeat(${data.length}, 1fr) ${data.length < 12 ? '200px' : ''}`,
                      gap: '12px',
                      marginBottom: '16px',
                      minWidth: `${200 + data.length * 300 + (data.length < 12 ? 200 : 0)}px`
                    }}
                  >
                    <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                      <Text fz={12} fw={600} c='secondary.7'>
                        Urgency Level
                      </Text>
                    </Paper>
                    {competencyData.map((competency, idx) => (
                      <Paper
                        key={`urgency-${ids[idx]}`}
                        withBorder
                        p='sm'
                        style={{
                          ...getHighlightStyle(
                            shouldHighlight(
                              'urgencyDesc',
                              competency?.urgencyDesc,
                              data,
                              competencyData
                            )
                          )
                        }}
                      >
                        {competency?.urgencyDesc ? (
                          <Badge
                            size='sm'
                            color={competency.urgencyDesc.includes('Urgent') ? 'red' : 'yellow'}
                            variant='light'
                          >
                            {competency.urgencyDesc}
                          </Badge>
                        ) : (
                          <Text fz={12} c='secondary.7'>
                            -
                          </Text>
                        )}
                      </Paper>
                    ))}
                    {data.length < 12 && <div></div>}
                  </div>
                </>
              )}

              {/* Field Groups in Table Format */}
              {fieldGroups.map((group) => (
                <div key={group.title} style={{ marginBottom: '24px' }}>
                  <Paper
                    p='sm'
                    mb='xs'
                    style={{ background: 'linear-gradient(90deg, #fff3e0 0%, #f8f9fa 100%)' }}
                  >
                    <Text fz={14} fw={700} c='orange.7' tt='uppercase'>
                      {group.title}
                    </Text>
                  </Paper>

                  {group.fields.map((field) => (
                    <div
                      key={field.key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `200px repeat(${data.length}, 1fr) ${data.length < 12 ? '200px' : ''}`,
                        gap: '12px',
                        marginBottom: '8px',
                        minWidth: `${200 + data.length * 300 + (data.length < 12 ? 200 : 0)}px`
                      }}
                    >
                      <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                        <Text fz={12} fw={600} c='secondary.7'>
                          {field.label}
                        </Text>
                      </Paper>
                      {data.map((col, idx) => {
                        const value = col ? (col as any)[field.key] : null;
                        const displayValue = field.render ? field.render(value, col) : value;
                        const highlight = shouldHighlight(field.key, value, data);

                        return (
                          <Paper
                            key={`${field.key}-${ids[idx]}`}
                            withBorder
                            p='sm'
                            style={{
                              ...getHighlightStyle(highlight)
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Text fz={12} c='dark.7'>
                                {displayValue || '-'}
                              </Text>
                              {field.key === 'expectedParticipants' && (col?.budget ?? 0) > 0 && (
                                <Tooltip label='View participants' withArrow position='left'>
                                  <Button
                                    variant='subtle'
                                    size='compact-xs'
                                    onClick={() => openParticipantModal(ids[idx])}
                                    style={{ padding: 6, minWidth: 34 }}
                                  >
                                    <HiOutlineEye />
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </Paper>
                        );
                      })}
                      {data.length < 12 && <div></div>}
                    </div>
                  ))}
                </div>
              ))}

              {/* Learning Objectives Section */}
              <Paper
                p='sm'
                mb='xs'
                style={{ background: 'linear-gradient(90deg, #f3e5f5 0%, #f8f9fa 100%)' }}
              >
                <Text fz={14} fw={700} c='violet.7' tt='uppercase'>
                  Learning Objectives
                </Text>
              </Paper>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `200px repeat(${data.length}, 1fr) ${data.length < 12 ? '200px' : ''}`,
                  gap: '12px',
                  marginBottom: '16px',
                  minWidth: `${200 + data.length * 300 + (data.length < 12 ? 200 : 0)}px`
                }}
              >
                <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                  <Text fz={12} fw={600} c='secondary.7'>
                    Learning Objectives
                  </Text>
                </Paper>
                {data.map((col, idx) => (
                  <Paper
                    key={`objectives-${ids[idx]}`}
                    withBorder
                    p='sm'
                    style={{ minHeight: '80px' }}
                  >
                    <Text fz={11} c='secondary.8' lineClamp={4}>
                      {col?.learningObjectives
                        ? stripHtmlAndDecode(col.learningObjectives).slice(0, 150)
                        : 'No objectives specified'}
                    </Text>
                  </Paper>
                ))}
                {data.length < 12 && <div></div>}
              </div>
            </div>
          </ScrollArea>
        )}
      </Card>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <Stack gap={4}>
            <Text fz={18} fw={700} c='dark.8'>
              Add Training to Compare
            </Text>
            <Text fz={13} c='secondary.6'>
              Select additional training to compare (up to 12 total)
            </Text>
          </Stack>
        }
        size='xl'
        padding='lg'
      >
        <Stack gap='md'>
          <TextInput
            placeholder='Search training by course name, code, or program...'
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setModalPage(1);
            }}
            rightSection={modalLoading ? <Loader size='xs' /> : undefined}
            size='md'
            styles={{
              input: {
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                '&:focus': {
                  borderColor: '#228be6',
                  boxShadow: '0 0 0 2px rgba(34, 139, 230, 0.2)'
                }
              }
            }}
          />

          <Group justify='space-between' align='center'>
            <Text fz={12} c='secondary.6'>
              {modalLoading ? 'Searching...' : `Found ${modalData?.totalRecords || 0} training(s)`}
            </Text>
            <Badge size='sm' variant='light' color='blue'>
              Selected: {ids.length}/12
            </Badge>
          </Group>

          <Paper withBorder radius='md' style={{ overflow: 'hidden' }}>
            <Table.ScrollContainer minWidth={700}>
              <Table striped highlightOnHover stickyHeader withRowBorders={false}>
                <Table.Thead bg='gray.0'>
                  <Table.Tr>
                    <Table.Th style={{ padding: '12px 16px' }}>
                      <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                        Course Code
                      </Text>
                    </Table.Th>
                    <Table.Th style={{ padding: '12px 16px' }}>
                      <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                        Course Name
                      </Text>
                    </Table.Th>
                    <Table.Th style={{ padding: '12px 16px' }}>
                      <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                        Program
                      </Text>
                    </Table.Th>
                    <Table.Th style={{ padding: '12px 16px' }}>
                      <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                        Category
                      </Text>
                    </Table.Th>
                    <Table.Th style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                        Action
                      </Text>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {modalLoading && (
                    <Table.Tr>
                      <Table.Td colSpan={5} style={{ padding: '24px', textAlign: 'center' }}>
                        <Center>
                          <Stack align='center' gap={12}>
                            <Loader size='md' variant='dots' />
                            <Text fz={14} c='secondary.6'>
                              Loading training data...
                            </Text>
                          </Stack>
                        </Center>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {!modalLoading && (modalData?.data?.length || 0) === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={5} style={{ padding: '40px', textAlign: 'center' }}>
                        <Stack align='center' gap={8}>
                          <Text fz={16} fw={600} c='secondary.7'>
                            No training found
                          </Text>
                          <Text fz={13} c='secondary.5'>
                            Try adjusting your search criteria
                          </Text>
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {!modalLoading &&
                    modalData?.data?.map((r) => {
                      const disabled = ids.includes(r.trainingRecommendationID) || ids.length >= 12;
                      const isSelected = ids.includes(r.trainingRecommendationID);

                      return (
                        <Table.Tr
                          key={r.trainingRecommendationID}
                          style={{
                            opacity: disabled && !isSelected ? 0.6 : 1,
                            backgroundColor: isSelected ? '#f8f9fa' : 'transparent'
                          }}
                        >
                          <Table.Td style={{ padding: '12px 16px' }}>
                            <Badge variant='light' color='blue' size='sm'>
                              {r.courseCode}
                            </Badge>
                          </Table.Td>
                          <Table.Td style={{ padding: '12px 16px' }}>
                            <Text fz={14} fw={500} lineClamp={2} style={{ maxWidth: 300 }}>
                              {r.courseName}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ padding: '12px 16px' }}>
                            <Text fz={13} c='secondary.7'>
                              {r.courseFrom || '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ padding: '12px 16px' }}>
                            <Text fz={13} c='secondary.5'>
                              -
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            {isSelected ? (
                              <Badge size='sm' color='teal' variant='light'>
                                ✓ Added
                              </Badge>
                            ) : (
                              <Button
                                size='xs'
                                variant='light'
                                disabled={disabled}
                                onClick={() => {
                                  if (disabled) return;
                                  const raw = localStorage.getItem(LOCAL_KEY);
                                  const arr: string[] = raw ? JSON.parse(raw) : [];
                                  if (arr.includes(r.trainingRecommendationID) || arr.length >= 12)
                                    return;
                                  const next = [...arr, r.trainingRecommendationID];
                                  localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
                                  setIds(next);
                                }}
                                style={{
                                  borderRadius: '6px',
                                  fontWeight: 600
                                }}
                              >
                                Add
                              </Button>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>

          <Group justify='space-between' mt='sm'>
            <Group gap={6}>
              <Text fz={12} c='secondary.6'>
                Page {modalPage} of {modalData?.totalPages || 1}
              </Text>
              <Text fz={12} c='secondary.4'>
                •
              </Text>
              <Text fz={12} c='secondary.6'>
                {(modalPage - 1) * modalSize + 1}-
                {Math.min(modalPage * modalSize, modalData?.totalRecords || 0)} of{' '}
                {modalData?.totalRecords || 0}
              </Text>
            </Group>
            <Group gap={6}>
              <Button
                size='xs'
                variant='default'
                disabled={modalPage <= 1 || modalLoading}
                onClick={() => setModalPage((p) => Math.max(1, p - 1))}
                style={{ borderRadius: '6px' }}
              >
                Previous
              </Button>
              <Button
                size='xs'
                variant='default'
                disabled={modalPage >= (modalData?.totalPages || 1) || modalLoading}
                onClick={() => setModalPage((p) => p + 1)}
                style={{ borderRadius: '6px' }}
              >
                Next
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Participant modal (view participants for a training) */}
      <Modal
        opened={participantModalOpen}
        onClose={() => {
          setParticipantModalOpen(false);
          setParticipantTrainingId(null);
        }}
        title={
          <Text fz={16} fw={700}>
            Participants
          </Text>
        }
        size='xl'
        padding='lg'
      >
        <Stack gap='sm'>
          <Text fz={13} c='secondary.6'>
            Showing participants for training ID: {participantTrainingId}
          </Text>

          <Paper withBorder radius='md' style={{ overflow: 'hidden' }}>
            <DataTables
              columns={participantColumns}
              data={participants}
              loading={isParticipantsPending}
              withPagination={false}
              isNumbering
              totalData={participants.length}
              emptyMessage='-'
            />
          </Paper>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default CourseCombineComparePage;
